import React, { useEffect, useState } from 'react';
import { Federation, MailTemplate } from "../typings";
import { Align, Row } from "../components/Flex";
import TextField from "../components/TextField";
import { FaAngleLeft, FaAngleRight, FaSort, FaSortDown, FaSortUp } from "react-icons/fa6";
import { toast } from "react-toastify";
import { useApi } from "../api-context";
import { useNavigate } from "react-router-dom";
import CreateByNameDialog from "../components/CreateByNameDialog";
import { createMailTemplate, fetchMailTemplates } from "./email-templates-api";
import TemplateRow from "./TemplateRow";

function EmailTemplatesPage() {
    const navigate = useNavigate();
    const api = useApi();
    const [total, setTotal] = useState(0);
    const [templates, setTemplates] = useState<MailTemplate[]>([]);

    const [page, setPage] = useState(0);
    const [nameFilter, setNameFilter] = useState('');
    const [orderBy, setOrderBy] = useState('id');
    const [orderDirection, setOrderDirection] = useState('ASC');
    const [dialogOpen, setDialogOpen] = useState(false);

    const loadMailTemplates = (page: number, orderBy: string, orderDirection: string, nameFilter?: string) => {
        fetchMailTemplates(api, { orderBy, orderDirection, page, nameFilter })
            .then(res => {
                setTotal(res.total);
                setTemplates(res.items);
            })
            .catch((reason) => {
                if (reason !== 'Unauthorized') {
                    toast.error('Failed to load mail templates');
                }
            });
    };

    const onFilter = (nameFilter: string) => {
        setPage(0);
        setNameFilter(nameFilter);
    }

    const onChangeOrder = (newOrderBy: string) => {
        setPage(0);
        if (newOrderBy === orderBy) {
            setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
            return;
        }
        setOrderBy(newOrderBy);
        setOrderDirection('ASC')
    }

    useEffect(() => {
        loadMailTemplates(page, orderBy, orderDirection, nameFilter || undefined);
    }, [orderBy, orderDirection, nameFilter, page]);

    const onCreateTemplate = (name: string) => {
        createMailTemplate(api, name)
            .then((id) => {
                navigate(`/template/${id}`)
                toast.success('Successfully created template');
            })
            .catch(() => toast.error('Failed to create template'))
            .finally(() => {
                setDialogOpen(false);
            })
    };

    const onOpenDialog = () => setDialogOpen(true);
    const onCloseDialog = () => setDialogOpen(false);

    const renderColumnHeader = (title: string, prop: string, flex: number) => {
        return (
            <div style={{ flex, display: 'flex' }}>
                <div className="column-header" onClick={() => onChangeOrder(prop)}>
                    <div>{title}</div>
                    {
                        orderBy !== prop
                            ? <FaSort/>
                            : orderDirection === 'ASC' ? <FaSortUp /> : <FaSortDown />
                    }
                </div>
            </div>
        );
    };

    const renderSearchHeader = () => {
        return (
            <Row align={Align.SPACE_BETWEEN} className="search-header">
                <TextField label="" value={nameFilter} setValue={onFilter} placeholder="Search by name"/>
                <div style={{ color: '#545452' }}>
                    {`Showing ${page * 10 + (total ? 1 : 0)}-${Math.min(total, (page + 1) * 10)} of ${total}`}
                </div>
            </Row>
        );
    };

    return (
        <div className="page-container drill-files-page">
            <Row gap={8} style={{marginBottom: '32px'}}>
                <div style={{ flex: 1 }} className="main-title">
                    Mail templates
                </div>
                <button
                    style={{ flex: 0, minWidth: '150px' }}
                    className="my-button"
                    onClick={onOpenDialog}
                >
                    CREATE
                </button>
            </Row>
            {renderSearchHeader()}
            <Row style={{ padding: '12px 16px', marginBottom: '12px' }}>
                {renderColumnHeader('Id', 'id', 1)}
                {renderColumnHeader('Name', 'name', 2)}
                {renderColumnHeader('Subject', 'subject', 2)}
                <div style={{ width: '28px' }}/>
            </Row>
            <div className="content-container" style={{ marginBottom: '32px', gap: '6px' }}>
                {templates.map((template) => (
                    <TemplateRow
                        key={template.id}
                        template={template}
                    />
                ))}
            </div>
            <Row align={Align.END} gap={4}>
                <FaAngleLeft
                    className="pager-icon"
                    onClick={() => setPage(Math.max(0, page - 1))}
                    size={20}
                    color="#545452"
                />
                <div style={{
                    fontSize: '16px',
                    fontFamily: 'InterRegular',
                    color: '#545452'
                }}>
                    {page + 1} of {Math.ceil(total / 10)}
                </div>
                <FaAngleRight
                    className="pager-icon"
                    size={20}
                    onClick={() => setPage(Math.min(Math.floor(total / 10), page + 1))}
                    color="#545452"
                />
            </Row>
            <CreateByNameDialog
                title="Create new template"
                open={dialogOpen}
                onClose={onCloseDialog}
                onCreate={onCreateTemplate}
            />
        </div>
    );
}

export default EmailTemplatesPage;
